import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Operadora {
  _id: {
    $oid: string;
  };
  code: string;
  status: boolean;
  showClient: boolean;
  showConsole: boolean;
  order: number;
  name: string;
  urlImag: string;
  description: string;
  date?: string;
}

export interface TipoOperador {
  _id: {
    $oid: string;
  };
  code: string;
  status: boolean;
  showClient: boolean;
  showConsole: boolean;
  order: number;
  name: string;
  operator: {
    $oid: string;
  };
  description: string;
  date?: string;
}

export interface Producto {
  _id: {
    $oid: string;
  };
  code: string;
  status: boolean;
  showClient: boolean;
  showConsole: boolean;
  order: number;
  name: string;
  operator: {
    $oid: string;
  };
  code_integation: string;
  operator_integation: string;
  type_operator_integation: string;
  type_operator: {
    $oid: string;
  };
  description: string;
  date?: string;
  invoice_plan: string[];
  min: number;
  max: number;
  params?: {
    sequence?: {
      BS?: {
        default: number;
        list: number[];
      };
    };
  };
}

export interface OperatorsResponse {
  resp: Operadora[];
}

export interface TypeOperatorsResponse {
  resp: TipoOperador[];
}

export interface ProductsResponse {
  resp: Producto[];
}

// Interfaces para respuestas de toggle status
export interface ToggleOperatorResponse {
  msg: string;
  operator: {
    code: string;
    name: string;
    status: boolean;
  };
}

export interface ToggleTypeOperatorResponse {
  msg: string;
  typeOperator: {
    code: string;
    name: string;
    status: boolean;
  };
}

export interface ToggleProductOperatorResponse {
  msg: string;
  productOperator: {
    code: string;
    name: string;
    status: boolean;
  };
}

export interface ToggleErrorResponse {
  msg: string;
  error: {
    code: number;
    msg: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RecargasService {

  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.BASE_LA;
  }

  /**
   * Obtiene todas las operadoras activas para mostrar en consola
   */
  getOperators = async (params: any = {}) => {
    const headers = { 'content-type': 'application/json' };
    const request = params;

    try {
      return lastValueFrom(
        this.httpClient.get<OperatorsResponse>(
          `${this.baseUrl}/api/invoice-credit/operators`,
          {
            headers: headers,
            params: request,
          }
        )
      );
    } catch (error) {
      console.error('Error getting operators:', error);
      throw error;
    }
  };

  /**
   * Filtra operadoras para mostrar solo las activas y visibles en consola
   */
  filterOperatorsForConsole(operadoras: Operadora[]): Operadora[] {
    return operadoras
      .filter(op => op.status && op.showConsole)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Construye la URL completa de la imagen de la operadora
   */
  getOperatorImageUrl(urlImag: string): string {
    if (!urlImag) {
      return '/assets/brandLogos/default.svg';
    }
    
    // Si ya tiene el dominio completo, lo devuelve tal como está
    if (urlImag.startsWith('http')) {
      return urlImag;
    }
    
    // Si es una ruta que empieza con /assets, la usa directamente
    if (urlImag.startsWith('/assets')) {
      return urlImag;
    }
    
    // Si es solo el nombre del archivo, busca en brandLogos
    if (!urlImag.includes('/')) {
      return `/assets/brandLogos/${urlImag}`;
    }
    
    // Si es una ruta relativa del servidor, la combina con BASE_LA
    return `${this.baseUrl}${urlImag}`;
  }

  /**
   * Obtiene los tipos de operadores para una operadora específica
   */
  getTypeOperators = async (operatorCode: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = { code: operatorCode };

    try {
      return lastValueFrom(
        this.httpClient.get<TypeOperatorsResponse>(
          `${this.baseUrl}/api/invoice-credit/type-operators`,
          {
            headers: headers,
            params: params,
          }
        )
      );
    } catch (error) {
      console.error('Error getting type operators:', error);
      throw error;
    }
  };

  /**
   * Filtra tipos de operadores para mostrar solo los activos y visibles en consola
   */
  filterTypeOperatorsForConsole(tiposOperadores: TipoOperador[]): TipoOperador[] {
    return tiposOperadores
      .filter(tipo => tipo.status && tipo.showConsole)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Obtiene los productos para una operadora y tipo de operador específicos
   */
  getProducts = async (operatorCode: string, typeOperatorCode: string) => {
    const headers = { 'content-type': 'application/json' };
    const params = { 
      operator: operatorCode,
      code: typeOperatorCode 
    };

    try {
      return lastValueFrom(
        this.httpClient.get<ProductsResponse>(
          `${this.baseUrl}/api/invoice-credit/products-operator`,
          {
            headers: headers,
            params: params,
          }
        )
      );
    } catch (error) {
      console.error('Error getting products:', error);
      throw error;
    }
  };

  /**
   * Filtra productos para mostrar solo los activos y visibles en consola
   */
  filterProductsForConsole(productos: Producto[]): Producto[] {
    return productos
      .filter(producto => producto.status && producto.showConsole)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Actualiza la secuencia de un producto operador específico o todos
   */
  updateProductSequence = async (sequence: any, code?: string) => {
    const headers = { 'content-type': 'application/json' };
    const body: any = { sequence };
    
    // Si se proporciona código, actualiza solo ese producto
    if (code) {
      body.code = code;
    }

    try {
      return lastValueFrom(
        this.httpClient.post<{res: boolean}>(
          `${this.baseUrl}/api/product-operator/sequence`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error updating product sequence:', error);
      throw error;
    }
  };

  /**
   * Cambia el estado (activo/inactivo) de un operador
   */
  toggleOperatorStatus = async (code: string, status: boolean) => {
    const headers = { 'content-type': 'application/json' };
    const body = { code, status };

    try {
      return lastValueFrom(
        this.httpClient.post<ToggleOperatorResponse>(
          `${this.baseUrl}/api/invoice-credit/operators/toggle-status`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error toggling operator status:', error);
      throw error;
    }
  };

  /**
   * Cambia el estado (activo/inactivo) de un tipo de operador
   */
  toggleTypeOperatorStatus = async (code: string, status: boolean) => {
    const headers = { 'content-type': 'application/json' };
    const body = { code, status };

    try {
      return lastValueFrom(
        this.httpClient.post<ToggleTypeOperatorResponse>(
          `${this.baseUrl}/api/invoice-credit/type-operators/toggle-status`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error toggling type operator status:', error);
      throw error;
    }
  };

  /**
   * Cambia el estado (activo/inactivo) de un producto operador
   */
  toggleProductOperatorStatus = async (code: string, status: boolean) => {
    const headers = { 'content-type': 'application/json' };
    const body = { code, status };

    try {
      return lastValueFrom(
        this.httpClient.post<ToggleProductOperatorResponse>(
          `${this.baseUrl}/api/invoice-credit/products-operator/toggle-status`,
          body,
          { headers: headers }
        )
      );
    } catch (error) {
      console.error('Error toggling product operator status:', error);
      throw error;
    }
  };
} 